import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import PrintIcon from '@mui/icons-material/PrintOutlined';
import SendIcon from '@mui/icons-material/SendOutlined';

import quotesApi from '../../../api/quotesApi';
import { getErrorMessage, getBlobErrorMessage } from '../../../utils/errorHandler';
import { printBlob, triggerBlobDownload } from '../../../utils/downloadFile';

const currencyPen = new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' });
const currencyUsd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export default function QuoteDetailDialog({ open, onClose, quote, statuses, onChanged }) {
  const { enqueueSnackbar } = useSnackbar();
  const [statusId, setStatusId] = useState('');
  const [emailTo, setEmailTo] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    if (quote) {
      setStatusId(quote.status?.id || '');
      setEmailTo(quote.customer_email || '');
    }
  }, [quote]);

  if (!quote) {
    return null;
  }

  const handleStatusChange = async (event) => {
    const newStatusId = event.target.value;
    setStatusId(newStatusId);
    setSavingStatus(true);
    try {
      await quotesApi.updateQuoteStatus(quote.id, newStatusId);
      enqueueSnackbar('Estado actualizado correctamente.', { variant: 'success' });
      onChanged();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo actualizar el estado.'), { variant: 'error' });
    } finally {
      setSavingStatus(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await quotesApi.downloadQuotePdf(quote.id);
      triggerBlobDownload(response.data, `cotizacion-${quote.uuid}.pdf`);
    } catch (error) {
      enqueueSnackbar(await getBlobErrorMessage(error, 'No se pudo descargar el PDF.'), { variant: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  // Proforma en formato ticket de 80mm, directo a la tiketera (sin descargar el archivo).
  const handlePrintTicket = async () => {
    setPrinting(true);
    try {
      const response = await quotesApi.downloadQuotePdf(quote.id, 'ticket');
      printBlob(response.data);
    } catch (error) {
      enqueueSnackbar(await getBlobErrorMessage(error, 'No se pudo imprimir la proforma.'), { variant: 'error' });
    } finally {
      setPrinting(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailTo) {
      enqueueSnackbar('Ingresa un correo de destino.', { variant: 'warning' });
      return;
    }
    setSendingEmail(true);
    try {
      const { data } = await quotesApi.sendQuoteEmail(quote.id, emailTo);
      enqueueSnackbar(data.message || 'Cotización enviada por correo.', { variant: 'success' });
      onChanged();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo enviar el correo.'), { variant: 'error' });
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Cotización N.° {quote.id}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary">
                Cliente
              </Typography>
              <Typography>{quote.customer_name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {quote.customer_email} {quote.customer_phone ? `· ${quote.customer_phone}` : ''}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary">
                Tipo de cambio usado
              </Typography>
              <Typography>S/ {quote.exchange_rate_used.toFixed(4)} por US$ 1.00</Typography>
              {quote.last_emailed_at && (
                <Typography variant="caption" color="text.secondary">
                  Último envío: {new Date(quote.last_emailed_at).toLocaleString('es-PE')}
                </Typography>
              )}
            </Grid>
          </Grid>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Producto</TableCell>
                <TableCell align="right">Cant.</TableCell>
                <TableCell align="right">P. Unit. (S/)</TableCell>
                <TableCell align="right">P. Unit. (US$)</TableCell>
                <TableCell align="right">Subtotal (S/)</TableCell>
                <TableCell align="right">Subtotal (US$)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {quote.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.product?.name || item.description || 'Producto eliminado'}</TableCell>
                  <TableCell align="right">{item.quantity}</TableCell>
                  <TableCell align="right">{currencyPen.format(item.unit_price_pen)}</TableCell>
                  <TableCell align="right">{currencyUsd.format(item.unit_price_usd)}</TableCell>
                  <TableCell align="right">{currencyPen.format(item.subtotal_pen)}</TableCell>
                  <TableCell align="right">{currencyUsd.format(item.subtotal_usd)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Stack sx={{ justifyContent: 'flex-end' }} direction="row" spacing={4}>
            <Typography variant="h6">Total: {currencyPen.format(quote.subtotal_pen)}</Typography>
            <Typography variant="h6">{currencyUsd.format(quote.subtotal_usd)}</Typography>
          </Stack>

          <Divider />

          <Grid sx={{ alignItems: 'center' }} container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                select
                fullWidth
                label="Estado"
                value={statusId}
                onChange={handleStatusChange}
                disabled={savingStatus}
              >
                {statuses.map((status) => (
                  <MenuItem key={status.id} value={status.id}>
                    {status.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField
                fullWidth
                label="Enviar cotización a"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleSendEmail}
                disabled={sendingEmail}
              >
                {sendingEmail ? 'Enviando...' : 'Enviar'}
              </Button>
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrintTicket} disabled={printing}>
          {printing ? 'Generando...' : 'Imprimir ticket'}
        </Button>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload} disabled={downloading}>
          {downloading ? 'Generando PDF...' : 'Descargar PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

QuoteDetailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  quote: PropTypes.object,
  statuses: PropTypes.array.isRequired,
  onChanged: PropTypes.func.isRequired,
};

QuoteDetailDialog.defaultProps = {
  quote: null,
};
